import {
  type FragmentType,
  useFragment as readFragment,
  TaxonomyNodeFieldsFragmentDoc,
  type TaxonomyTreeQuery,
  type TaxonomyType,
} from '@revelio/data-access';
import type { TreeData } from '@revelio/ui';

type TaxonomyNodeWithChildren = FragmentType<
  typeof TaxonomyNodeFieldsFragmentDoc
> & {
  children?: readonly TaxonomyNodeWithChildren[] | null;
};

export type TaxonomyLevelMetadata =
  TaxonomyTreeQuery['taxonomy']['levels'][number];

export type NormalizedTaxonomyTree = {
  levels: TaxonomyLevelMetadata[];
  nodes: TreeData[];
};

const EMPTY_NORMALIZED_TAXONOMY_TREE: NormalizedTaxonomyTree = {
  levels: [],
  nodes: [],
};

type TaxonomyTreePayload = NonNullable<TaxonomyTreeQuery['taxonomy']>;

const normalizedTaxonomyTreeByPayload = new WeakMap<
  TaxonomyTreePayload,
  NormalizedTaxonomyTree
>();

const capitalizeFirstCharacter = (value: string) => {
  if (value.length === 0) {
    return value;
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const getTaxonomyCacheKey = ({
  taxonomyType,
  taxonomyId,
  taxonomyVersion,
  levels,
  includeTitles,
}: {
  taxonomyType: TaxonomyType;
  taxonomyId: number;
  taxonomyVersion?: string | null;
  levels?: readonly number[] | null;
  includeTitles?: boolean;
}) => {
  const sortedLevels = [...(levels ?? [])].sort((a, b) => a - b).join(',');
  return [
    taxonomyType,
    taxonomyId,
    taxonomyVersion ?? 'latest',
    sortedLevels || 'all',
    ...(includeTitles === undefined
      ? []
      : [includeTitles ? 'titles' : 'plain']),
  ].join(':');
};

export const normalizeTaxonomyNodes = (
  nodes: readonly TaxonomyNodeWithChildren[],
  depth = 0
): TreeData[] => {
  return nodes
    .map((node): TreeData => {
      const nodeFields = readFragment(TaxonomyNodeFieldsFragmentDoc, node);
      const children =
        node.children && node.children.length > 0
          ? normalizeTaxonomyNodes(node.children, depth + 1)
          : undefined;
      return {
        id: String(nodeFields.id),
        name: nodeFields.name,
        nodeCount: nodeFields.count,
        depth,
        ...(nodeFields.titles != null
          ? { taxonomyTitles: nodeFields.titles }
          : {}),
        ...(children ? { children } : {}),
      };
    })
    .sort((a, b) => (b.nodeCount ?? 0) - (a.nodeCount ?? 0));
};

export const normalizeTaxonomyTree = (
  taxonomy: TaxonomyTreeQuery['taxonomy'] | null | undefined
): NormalizedTaxonomyTree => {
  if (!taxonomy) {
    return EMPTY_NORMALIZED_TAXONOMY_TREE;
  }

  const cached = normalizedTaxonomyTreeByPayload.get(taxonomy);
  if (cached !== undefined) {
    return cached;
  }

  const normalized: NormalizedTaxonomyTree = {
    levels: taxonomy.levels,
    nodes: normalizeTaxonomyNodes(taxonomy.nodes),
  };
  normalizedTaxonomyTreeByPayload.set(taxonomy, normalized);
  return normalized;
};

export type TaxonomyLeafIdsByDepth = readonly ReadonlyMap<
  number,
  readonly number[]
>[];

const taxonomyLeafIdsByPayload = new WeakMap<
  TaxonomyTreePayload,
  TaxonomyLeafIdsByDepth
>();

/**
 * Leaf ids for every node of a tree, by depth then node id. The API fills
 * `leaf_ids` only on the deepest requested level, so each ancestor gets the
 * union of its descendants' ids.
 */
export const getTaxonomyLeafIdsByDepth = (
  taxonomy: TaxonomyTreeQuery['taxonomy'] | null | undefined
): TaxonomyLeafIdsByDepth => {
  if (!taxonomy) {
    return [];
  }

  const cached = taxonomyLeafIdsByPayload.get(taxonomy);
  if (cached !== undefined) {
    return cached;
  }

  const byDepth: Map<number, number[]>[] = [];
  const collect = (
    nodes: readonly TaxonomyNodeWithChildren[],
    depth: number
  ): number[] => {
    const leafIdsByNodeId = byDepth[depth] ?? new Map<number, number[]>();
    byDepth[depth] = leafIdsByNodeId;
    return nodes.flatMap((node) => {
      const { id, leaf_ids } = readFragment(
        TaxonomyNodeFieldsFragmentDoc,
        node
      );
      const leafIds = [
        ...(leaf_ids ?? []),
        ...(node.children ? collect(node.children, depth + 1) : []),
      ];
      leafIdsByNodeId.set(id, leafIds);
      return leafIds;
    });
  };
  collect(taxonomy.nodes, 0);

  taxonomyLeafIdsByPayload.set(taxonomy, byDepth);
  return byDepth;
};

export type DepthToLabelMap = Record<number, string>;

/**
 * Depth → label map for taxonomy levels, keyed by each level's position in the
 * id-sorted ladder (matching the tree's render depth). Shared by the custom
 * skill and activity level-label hooks.
 */
export const buildLevelLabelsByDepth = (
  levels: readonly TaxonomyLevelMetadata[]
): DepthToLabelMap | null => {
  if (levels.length === 0) return null;
  const sorted = [...levels].sort((a, b) => a.id - b.id);
  const map: DepthToLabelMap = {};
  sorted.forEach((level, index) => {
    const label = level.label ?? level.tag ?? `Level ${level.id}`;
    if (label !== '') map[index] = label;
  });
  return Object.keys(map).length > 0 ? map : null;
};

export const getTaxonomyBranchLabels = (
  levels: readonly TaxonomyLevelMetadata[]
) => {
  return [...levels]
    .sort((a, b) => a.id - b.id)
    .map((level) => ({
      label: capitalizeFirstCharacter(
        level.label ?? level.tag ?? `Level ${level.id}`
      ),
    }));
};
